import { Router } from '@angular/router';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { getPlatformLogoPath } from 'src/app/common/platformUtils';
import { Platform } from 'src/app/models/api/common';
import { PlaybookStatusSelectOptions } from 'src/app/models/selectOptions/playbookStatusSelectOptions.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ViolationsTableColumnsModel } from 'src/app/models/tableColumns/violationsTableColumn.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { IntegrationModel } from '../../../api';
import { ISelectOption, PlatformSelectOptions } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { TextFilter } from '../filtersTypes/textFilter.model';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';

export class PlaybooksFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public integrationFilters: MultiSelectFilters;
  public platformFilters: MultiSelectFilters;
  public playbookStatus: MultiSelectFilters;
  public lastExecutionSince: DateFilter;
  public lastExecutionUntil: DateFilter;
  public search: TextFilter;

  public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    undefined
  );
  private _defaultSortColumn: SortColumn | undefined = undefined;

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _permissionsService: PermissionsService
  ) {
    this._initializeFilters();

    this._integrationsOnUpdate();
    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());

    this._initDefaultSortColumn();
  }

  public getDynamicFilters(): string[] {
    return [];
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [
      ...this.integrationFilters.getSelectedOptions(),
      ...this.platformFilters.getSelectedOptions(),
      ...this.playbookStatus.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      platform: this.platformFilters.getSelectedFiltersValues(),
      integration: this.integrationFilters.getSelectedFiltersValues(),
      playbookStatus: this.playbookStatus.getSelectedFiltersValues(),
      lastExecutionSince: this.lastExecutionSince.getFilterValue(),
      lastExecutionUntil: this.lastExecutionUntil.getFilterValue(),
      search: this.search.getFilterValue(),
    } as ISelectedFilters;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    values: ISelectOption[] | ISelectOption | Date | boolean | string | undefined
  ): void {
    let needUpdate = false;
    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.LastExecutionSince:
          this.lastExecutionSince.update(values as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.LastExecutionUntil:
          this.lastExecutionUntil.update(values as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.Search:
          this.search.update(values as string | undefined);
          needUpdate = true;
          break;
        default:
          break;
      }
    } else {
      if (values instanceof Array) {
        if (values.length == 0) {
          needUpdate = this._clearAllMultiSelectFiltersIfNeeded();
        } else {
          const optionsByCategory: {
            [key: string]: ISelectOption[];
          } = {};
          values.forEach((option) => {
            if (option.filterType) {
              if (option.filterType in optionsByCategory) {
                optionsByCategory[option.filterType].push(option);
              } else {
                optionsByCategory[option.filterType] = [option];
              }
            }
          });
          needUpdate =
            this.platformFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Platform] || []) || needUpdate;
          needUpdate =
            this.playbookStatus.updateIfNeeded(optionsByCategory[FilterTypesEnum.PlaybookStatus] || []) || needUpdate;
          needUpdate =
            this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
        }
      }
    }

    if (needUpdate) {
      // this.handleSortedColumnOnFilter(filterToUpdate)
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: { q: filtersQuery },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach((option) => {
      if (option.filterType) {
        if (option.filterType in optionsIdsByCategory) {
          optionsIdsByCategory[option.filterType].add(option.id);
        } else {
          optionsIdsByCategory[option.filterType] = new Set([option.id]);
        }
      }
    });

    if (optionsIdsByCategory[FilterTypesEnum.Platform]) {
      this.platformFilters.updateIfNeeded(
        this.platformFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Platform] &&
              !optionsIdsByCategory[FilterTypesEnum.Platform].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Integration] &&
              !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.PlaybookStatus]) {
      this.playbookStatus.updateIfNeeded(
        this.playbookStatus
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.PlaybookStatus] &&
              !optionsIdsByCategory[FilterTypesEnum.PlaybookStatus].has(selectedOption.id)
          )
      );
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: { q: filtersQuery },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getOptions$();

      case FilterTypesEnum.Platform:
        return this.platformFilters.getOptions$();

      case FilterTypesEnum.PlaybookStatus:
        return this.playbookStatus.getOptions$();

      default:
        throw new Error(`PlaybooksFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.Platform:
        return this.platformFilters.getSelectedFiltersIds();

      case FilterTypesEnum.Integration:
        return this.integrationFilters.getSelectedFiltersIds();

      case FilterTypesEnum.PlaybookStatus:
        return this.playbookStatus.getSelectedFiltersIds();

      default:
        throw new Error(`PlaybooksFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    let values;

    if ('lastExecutionSince' in keyValueFilters || this.lastExecutionSince.getFilterValue() !== undefined) {
      this.lastExecutionSince.update(
        keyValueFilters['lastExecutionSince'] && moment(keyValueFilters['lastExecutionSince'][0], 'x').isValid()
          ? moment(keyValueFilters['lastExecutionSince'][0], 'x').toDate()
          : undefined
      );
      needUpdate = true;
    }

    if ('lastExecutionUntil' in keyValueFilters || this.lastExecutionUntil.getFilterValue() !== undefined) {
      this.lastExecutionUntil.update(
        keyValueFilters['lastExecutionUntil'] && moment(keyValueFilters['lastExecutionUntil'][0], 'x').isValid()
          ? moment(keyValueFilters['lastExecutionUntil'][0], 'x').toDate()
          : undefined
      );
      needUpdate = true;
    }

    if ('search' in keyValueFilters || this.search.getFilterValue() !== undefined) {
      this.search.update(keyValueFilters['search'] ? keyValueFilters['search'][0] : undefined);
      needUpdate = true;
    }

    values = 'platform' in keyValueFilters ? keyValueFilters['platform'] : [];
    needUpdate = this.platformFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'playbookStatus' in keyValueFilters ? keyValueFilters['playbookStatus'] : [];
    needUpdate = this.playbookStatus.updateByValuesIfNeeded(values) || needUpdate;

    values = 'integration' in keyValueFilters ? keyValueFilters['integration'] : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: { q: filtersQuery },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public restoreSortColumnToDefaultIfNeeded() {
    this.sortedColumn$.next(this._defaultSortColumn);
  }

  private _clearAllMultiSelectFiltersIfNeeded(): boolean {
    const needUpdate = false;

    return (
      needUpdate ||
      this.platformFilters.updateIfNeeded([]) ||
      this.integrationFilters.updateIfNeeded([]) ||
      this.playbookStatus.updateIfNeeded([])
    );
  }

  private _initializeFilters() {
    this.platformFilters = new MultiSelectFilters(
      PlatformSelectOptions.filter((option) =>
        [Platform.powerplatform, Platform.workato].includes(option.id as Platform)
      )
    );
    this.playbookStatus = new MultiSelectFilters(PlaybookStatusSelectOptions);
    this.integrationFilters = new MultiSelectFilters([]);
    this.lastExecutionSince = new DateFilter();
    this.lastExecutionUntil = new DateFilter();
    this.search = new TextFilter();

    this._updateAllOptions();
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for playbook viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.PLAYBOOK_VIEWER
      );

      const integrationsOptionsMap: ISelectOption[] = [];
      const integrationsPlatformSet: Set<Platform> = new Set();

      filteredIntegrations.forEach((integration) => {
        integrationsOptionsMap.push({
          id: integration.id,
          value: integration.id,
          displayName: integration.name,
          filterType: FilterTypesEnum.Integration,
          categoryName: FilterTypesEnum.Integration,
          withoutBeautifyDisplayName: true,
          iconPath: getPlatformLogoPath(integration.platform),
        } as ISelectOption);

        integrationsPlatformSet.add(integration.platform);
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));

      const platformOptions = this.platformFilters
        .getOptions$()
        .value.filter((option) => integrationsPlatformSet.has(option.id as Platform));
      this.platformFilters.updateOptions(platformOptions);

      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.playbookStatus.getOptions$().value,
      ...this.platformFilters.getOptions$().value,
      ...this.integrationFilters.getOptions$().value,
    ]);
  }

  private _initDefaultSortColumn() {
    const defaultSortColumn = ViolationsTableColumnsModel.find((column) => column.propertyKey === 'createdBy');
    if (defaultSortColumn) {
      this._defaultSortColumn = {
        column: defaultSortColumn,
        isDesc: true,
      };
    }
  }
}
